/**
 * The parts the design uses more than once. Inline style objects, as the
 * handoff has them — with one change: every state a hover or a focus ring needs
 * is a class from `theme/globals.css`, because an inline style cannot express
 * one and the design feels dead without them.
 */
import { forwardRef } from 'react';
import type React from 'react';
import { color, font, radius, shadow } from '../theme/tokens';
import { Spark } from './Spark';
import { useTooltip } from './Tooltip';

export function PrimaryButton({
  children,
  onClick,
  full,
  size = 'md',
  href,
  type = 'button',
  /**
   * What the fill is made of.
   *
   * `accent` is the area's own teal, for the verb that *sells* something — buy
   * seats, invite somebody, create an organization. `ink` is the chrome's rule
   * (see `Shell.tsx`): the verb that simply commits what is in front of you is
   * black, so a page never has two filled shapes competing to be the important
   * one. `danger` is the third, for what cannot be undone.
   */
  tone = 'accent',
  disabled,
  reason,
  busy
}: {
  children: React.ReactNode;
  onClick?: () => void;
  full?: boolean;
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  type?: 'button' | 'submit';
  tone?: 'accent' | 'ink' | 'danger';
  disabled?: boolean;
  /** Why the verb is held (rule 6): it stays focusable at 40 % and says so in a tooltip. */
  reason?: string;
  /** The request it sent is running (§10.3): the label is the gerund, `aria-busy` says so. */
  busy?: boolean;
}): React.JSX.Element {
  const { trigger, tip } = useTooltip<HTMLButtonElement>(reason);
  const held = Boolean(reason);
  const style: React.CSSProperties = {
    fontFamily: 'inherit',
    fontSize: size === 'sm' ? 13 : size === 'lg' ? 15 : 14,
    fontWeight: 500,
    // White lettering on both fills: ink and the destructive red #C4282D (5.69:1).
    // Since the 2026-10 redesign the primary pill is ink, as on the site, and
    // teal is never a fill: `accent` is kept as a name and drawn as `ink`.
    color: '#FFFFFF',
    background: tone === 'danger' ? color.red : color.ink,
    border: 0,
    padding: size === 'sm' ? '8px 16px' : size === 'lg' ? '13px 22px' : '10px 18px',
    borderRadius: radius.chip,
    cursor: disabled || held ? 'default' : 'pointer',
    opacity: disabled || held ? 0.4 : 1,
    width: full ? '100%' : undefined,
    textAlign: 'center',
    display: 'inline-block',
    textDecoration: 'none'
  };
  return href ? (
    <a className="btn-primary" href={href} data-press="" style={style}>
      {children}
    </a>
  ) : (
    <>
      <button
        className="btn-primary"
        type={type}
        onClick={
          held
            ? (event) => {
                // A held submit must not send the form either.
                event.preventDefault();
              }
            : onClick
        }
        disabled={disabled}
        aria-disabled={held || undefined}
        aria-busy={busy || undefined}
        data-press=""
        {...trigger}
        style={style}
      >
        {children}
      </button>
      {tip}
    </>
  );
}

export function GhostButton({
  children,
  onClick,
  full,
  size = 'md',
  href,
  disabled
}: {
  children: React.ReactNode;
  onClick?: () => void;
  full?: boolean;
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  /** For the verbs that are busy rather than absent — a read in flight, mostly. */
  disabled?: boolean;
}): React.JSX.Element {
  const style: React.CSSProperties = {
    fontFamily: 'inherit',
    fontSize: size === 'sm' ? 13 : size === 'lg' ? 15 : 13.5,
    fontWeight: 500,
    color: color.ink,
    background: color.surface,
    border: `1px solid ${color.lineStrong}`,
    padding: size === 'sm' ? '8px 16px' : size === 'lg' ? '13px 22px' : '10px 18px',
    borderRadius: radius.chip,
    cursor: 'pointer',
    width: full ? '100%' : undefined,
    display: 'inline-block',
    textAlign: 'center',
    textDecoration: 'none',
    ...(disabled ? { opacity: 0.4, cursor: 'default' } : {})
  };
  return href ? (
    <a className="btn-ghost" href={href} data-press="" style={style}>
      {children}
    </a>
  ) : (
    <button className="btn-ghost" type="button" onClick={onClick} disabled={disabled} data-press="" style={style}>
      {children}
    </button>
  );
}

export function Card({
  children,
  padding = 26,
  accent = false,
  style
}: {
  children: React.ReactNode;
  padding?: number;
  accent?: boolean;
  style?: React.CSSProperties;
}): React.JSX.Element {
  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${accent ? color.accent : color.line}`,
        borderRadius: radius.xxl,
        padding,
        ...style
      }}
    >
      {children}
    </div>
  );
}

/** The mono eyebrow above a section or a card. */
export function Kicker({ children, onDark = false }: { children: React.ReactNode; onDark?: boolean }): React.JSX.Element {
  return (
    <div
      style={{
        fontFamily: font.mono,
        fontSize: 10.5,
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        color: onDark ? color.darkDim : color.inkFaint
      }}
    >
      {children}
    </div>
  );
}

/** Dark placeholder standing in for a capture that has not been shot yet. */
export function MediaSlot({ label, ratio = '16/9' }: { label: string; ratio?: string }): React.JSX.Element {
  return (
    <div
      style={{
        borderRadius: radius.xl,
        background: color.dark,
        border: `1px solid ${color.darkRaised}`,
        aspectRatio: ratio === 'auto' ? undefined : ratio,
        height: ratio === 'auto' ? '100%' : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `repeating-linear-gradient(135deg, #12171F 0 12px, ${color.dark} 12px 24px)`
      }}
    >
      <span
        style={{
          fontFamily: font.mono,
          fontSize: 12,
          color: color.darkDim,
          border: `1px dashed ${color.darkLine}`,
          padding: '9px 14px',
          borderRadius: radius.md,
          textAlign: 'center'
        }}
      >
        [ {label} ]
      </span>
    </div>
  );
}

/**
 * THE navigation row, ported from the desktop navigator's `stageRow` (§1.6):
 * 40px tall, 12px inset, 11px gap, radius 9. Selected is a fill and a heavier
 * weight — no accent bar, no left border — and a hover that stays quieter than
 * the selection. On the dark sidebar the selected row bleeds into the stage:
 * square right corners, negative right margin, so the fill runs to the content.
 */
export function NavRow({
  label,
  icon,
  active,
  onClick,
  theme = 'dark',
  bleed = true,
  height = 40
}: {
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  theme?: 'dark' | 'light';
  bleed?: boolean;
  height?: number;
}): React.JSX.Element {
  const dark = theme === 'dark';
  const base: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: icon ? 11 : 0,
    minHeight: height,
    width: '100%',
    padding: active && bleed ? '0 24px 0 12px' : '0 12px',
    cursor: 'pointer',
    border: 0,
    textAlign: 'left',
    fontFamily: 'inherit',
    borderRadius: active && bleed ? '9px 0 0 9px' : radius.lg,
    marginRight: active && bleed ? -12 : 0
  };
  const on: React.CSSProperties = dark
    ? { background: color.darkPanel, color: color.darkText }
    : {
        background: bleed ? color.surface : color.surfaceAlt,
        color: color.ink,
        boxShadow: bleed ? shadow.card : `inset 0 0 0 1px ${color.line}`
      };
  const off: React.CSSProperties = { background: 'transparent', color: dark ? color.darkMuted : color.inkSubtle };

  return (
    <button
      type="button"
      className={active ? undefined : dark ? 'nav-row-dark' : 'nav-row-light'}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      style={{ ...base, ...(active ? on : off) }}
    >
      {icon && <span style={{ flex: 'none', display: 'flex', opacity: active ? 0.85 : dark ? 0.85 : 0.7 }}>{icon}</span>}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 13.5,
          fontWeight: active ? 600 : 400,
          lineHeight: 1.35
        }}
      >
        {label}
      </span>
    </button>
  );
}

export function NavGroupLabel({ children, onDark = false }: { children: React.ReactNode; onDark?: boolean }): React.JSX.Element {
  return (
    <div
      style={{
        fontFamily: font.mono,
        fontSize: 10.5,
        fontWeight: 500,
        letterSpacing: '.11em',
        textTransform: 'uppercase',
        color: onDark ? color.darkFaint : color.inkFaint,
        padding: '0 12px 6px'
      }}
    >
      {children}
    </div>
  );
}

/* ── the parts only the member area has ─────────────────────────────────── */

export type BadgeTone = 'ok' | 'warn' | 'off' | 'bad';

const TONES: Record<BadgeTone, [string, string]> = {
  ok: [color.okText, color.okBg],
  warn: [color.warnText, color.warnBg],
  off: [color.offText, color.offBg],
  bad: [color.badText, color.badBg]
};

/** Pill badge — every status in every table is one of these four tones. */
export function Badge({ tone = 'ok', children }: { tone?: BadgeTone; children: React.ReactNode }): React.JSX.Element {
  const [foreground, background] = TONES[tone];
  return (
    <span
      style={{
        fontFamily: font.mono,
        fontSize: 11.5,
        color: foreground,
        background,
        padding: '5px 9px',
        borderRadius: radius.chip,
        justifySelf: 'start',
        // A chip is one word, however narrow the column it lands in. Without
        // this, "no seat" broke over two lines and the rounded corners closed
        // around it into a circle with half the word in each half.
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </span>
  );
}

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input(
  props,
  ref
): React.JSX.Element {
  return (
    <input
      {...props}
      ref={ref}
      style={{
        width: '100%',
        fontFamily: 'inherit',
        fontSize: 14,
        padding: '11px 12px',
        border: `1px solid ${color.lineInput}`,
        borderRadius: radius.lg,
        background: color.page,
        color: color.ink,
        ...props.style
      }}
    />
  );
});

export function Field({
  label,
  children,
  htmlFor
}: {
  label: string;
  children: React.ReactNode;
  htmlFor?: string;
}): React.JSX.Element {
  return (
    <div>
      <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 12.5, color: color.inkMuted, marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

/** N cells, 1px gutters over the border colour — no per-cell borders. */
export function StatStrip({
  items,
  columns,
  valueSize = 26
}: {
  /** `spark` draws the tile's trend beside the figure and `note` says what the figure is made of (§42.2). */
  items: { label: string; value: string; delta?: string; deltaTone?: 'ok' | 'muted'; spark?: (number | null)[]; note?: string }[];
  columns?: number;
  valueSize?: number;
}): React.JSX.Element {
  return (
    <div
      className="al-quad"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns ?? items.length}, minmax(0,1fr))`,
        // Tiles 8 px apart on the site's tile grey (bibliotheque §29).
        gap: 8
      }}
    >
      {items.map((item) => (
        <div key={item.label} style={{ background: color.surfaceAlt, padding: 20, borderRadius: radius.xxl, fontVariantNumeric: 'tabular-nums' }}>
          <div style={{ fontSize: 12.5, color: color.inkFaint }}>{item.label}</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8, marginTop: 6 }}>
            <div style={{ fontSize: valueSize, letterSpacing: '-.02em', fontWeight: 600 }}>{item.value}</div>
            {item.spark && <Spark values={item.spark} />}
          </div>
          {item.delta && (
            <div
              style={{
                fontSize: 12,
                color: item.deltaTone === 'ok' ? color.accentText : color.inkFaint,
                marginTop: 6
              }}
            >
              {item.delta}
            </div>
          )}
          {item.note && <div style={{ fontSize: 12.5, color: color.inkMuted, marginTop: 4 }}>{item.note}</div>}
        </div>
      ))}
    </div>
  );
}

/** Left-ruled note. Two tones only: teal (informative) and amber (caution). */
export function Callout({
  tone = 'teal',
  title,
  children
}: {
  tone?: 'teal' | 'amber';
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const teal = tone === 'teal';
  return (
    <div
      style={{
        // No accent rail since the redesign: a grey note for information, a
        // warm one for what needs doing (lot 8).
        background: teal ? color.surfaceAlt : color.warnBg,
        padding: '16px 18px',
        borderRadius: radius.xl,
        margin: '26px 0'
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: teal ? color.accentText : color.warnText, marginBottom: 5 }}>
        {title}
      </div>
      <div style={{ fontSize: 14.5, color: color.inkBody }}>{children}</div>
    </div>
  );
}

/**
 * How wide a table's rows stay on a phone, where the table scrolls in its frame
 * (§42.9, 390): each fixed column at its width, each flexible one at 160, the
 * 16 gaps and the 12 insets, and never under 640. A flexible column squeezed to
 * nothing by the fixed ones used to hide the member's name on Members.
 */
function phoneWidth(columns: string): number {
  const cells = columns.match(/[^\s(]+(\([^)]*\))?/g) ?? [];
  const fixed = cells.reduce((sum, cell) => sum + (/^\d+(\.\d+)?px$/.test(cell) ? parseFloat(cell) : 160), 0);
  return Math.max(640, Math.ceil(fixed + 16 * Math.max(0, cells.length - 1) + 24));
}

/**
 * One table recipe for the whole area (§3.4): a white card, a mono uppercase
 * header row, and body rows on the same grid divided by the secondary surface.
 */
export function Table({
  columns,
  header,
  toolbar,
  frame,
  children
}: {
  columns: string;
  header: string[];
  toolbar?: React.ReactNode;
  /** A long table's fixed frame (§42.9): the rows scroll inside it under a sticky header. */
  frame?: number | string;
  children: React.ReactNode;
}): React.JSX.Element {
  const head = (
    <div
      className="al-table-head"
      style={{
        display: 'grid',
        gridTemplateColumns: columns,
        gap: 16,
        alignItems: 'center',
        minHeight: 36,
        padding: '0 12px',
        borderBottom: `1px solid ${color.lineStrong}`,
        fontFamily: font.mono,
        fontSize: 10.5,
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        color: color.inkFaint,
        ...(frame ? { position: 'sticky', top: 0, zIndex: 1, background: color.surface } : {})
      }}
    >
      {header.map((cell, index) => (
        <span key={index}>{cell}</span>
      ))}
    </div>
  );
  return (
    // A dense table on hairlines, not in a box (redesign §27): a strong rule
    // above, the header in mono kickers, rows 40 high on #E0E0E5.
    <div
      className="al-table-scroll"
      style={{
        ['--al-table-min' as string]: `${phoneWidth(columns)}px`,
        background: color.surface,
        borderTop: `1px solid ${color.lineStrong}`
      }}
    >
      {toolbar && (
        <div
          className="al-table-toolbar"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '12px 0',
            borderBottom: `1px solid ${color.line}`,
            flexWrap: 'wrap'
          }}
        >
          {toolbar}
        </div>
      )}
      {frame ? (
        <div className="al-table-frame" style={{ maxHeight: frame, overflowY: 'auto', scrollbarGutter: 'stable' }}>
          {head}
          {children}
        </div>
      ) : (
        <>
          {head}
          {children}
        </>
      )}
    </div>
  );
}

export function Row({ columns, children }: { columns: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      className="al-table-row"
      style={{
        display: 'grid',
        gridTemplateColumns: columns,
        gap: 16,
        minHeight: 40,
        padding: '8px 12px',
        borderBottom: `1px solid ${color.line}`,
        fontSize: 13.5,
        alignItems: 'center'
      }}
    >
      {children}
    </div>
  );
}

/** A borderless row action. Destructive ones are red and say what they do. */
export function RowAction({
  children,
  onClick,
  danger,
  reason,
  label
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  /** The accessible name when the visible word alone is ambiguous (« Open: 2 invitations not taken », §40.3). */
  label?: string;
  /** Why the action is held: it stays focusable at 40 % and says so (rule 6). */
  reason?: string;
}): React.JSX.Element {
  const { trigger, tip } = useTooltip<HTMLButtonElement>(reason);
  return (
    <>
      <button
        type="button"
        onClick={reason ? undefined : onClick}
        aria-disabled={reason ? true : undefined}
        aria-label={label}
        {...trigger}
        style={{
          background: 'none',
          border: 0,
          padding: 0,
          cursor: reason ? 'default' : 'pointer',
          opacity: reason ? 0.4 : 1,
          fontFamily: 'inherit',
          fontSize: 13,
          color: danger ? color.red : color.accentText,
          textAlign: 'left'
        }}
      >
        {children}
      </button>
      {tip}
    </>
  );
}

/** An empty state that says what to do next, never just "nothing here". */
export function Empty({ title, children }: { title: string; children?: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        border: `1px solid ${color.line}`,
        background: color.surface,
        borderRadius: radius.xxl,
        padding: '34px 26px',
        textAlign: 'center'
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 13.5, color: color.inkSubtle, marginTop: 8, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

/**
 * A status in a dense table: a dot and a word in the tone's colour, the state in
 * clear under it (redesign §41.1) — never a coloured pill, and never the colour
 * alone. `ring` draws the dot hollow, for a state that is neither good nor bad.
 */
export function StatusCell({
  tone,
  word,
  detail,
  ring = false
}: {
  tone: 'ok' | 'warn' | 'bad' | 'off';
  word: string;
  detail?: string;
  ring?: boolean;
}): React.JSX.Element {
  const ink = { ok: color.okText, warn: color.warnText, bad: color.badText, off: color.inkMuted }[tone];
  const dot = { ok: color.accent, warn: '#C9A03A', bad: color.red, off: color.lineInput }[tone];
  return (
    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: ink, whiteSpace: 'nowrap' }}>
        <span
          aria-hidden="true"
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            flex: 'none',
            background: ring ? 'transparent' : dot,
            boxShadow: ring ? `inset 0 0 0 1.5px ${dot}` : 'none'
          }}
        />
        {word}
      </span>
      {detail && <span style={{ fontSize: 12.5, color: color.inkFaint, marginLeft: 14, whiteSpace: 'nowrap' }}>{detail}</span>}
    </span>
  );
}
